import { authenticatedFetch } from './api';

/**
 * Load an image with authentication and return an object URL
 * The caller is responsible for revoking the object URL when done
 * 
 * @param url - The image URL to load
 * @returns Promise<string> - Object URL that can be used as img.src
 */
export async function loadAuthenticatedImage(url: string): Promise<string> {
  const response = await authenticatedFetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to load image: ${response.statusText}`);
  }
  
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Preload an image with authentication (for caching)
 * This doesn't return an object URL, just loads the image into browser cache
 * 
 * @param url - The image URL to preload
 */
export async function preloadAuthenticatedImage(url: string): Promise<void> {
  const response = await authenticatedFetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to preload image: ${response.statusText}`);
  }
  
  // Convert to blob and create object URL, then load it into an Image
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl); // Clean up
      resolve();
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl); // Clean up
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}

